/**
 * Cadastros → Representantes: a equipe.
 * - Novo representante: nome e, se já tiver, o código do Winthor e o nome no SAP
 *   (tudo de uma vez). A foto entra depois, em "Editar".
 * - Editar: nome, ativo, entra no ranking, foto (recorte redondo) e "Excluir"
 *   (só para cadastro feito por engano: quem tem meta ou código não pode, o
 *   histórico ficaria sem dono).
 * - Saiu da equipe: abre a troca (telas/cadastros/TrocaDeRepresentante.tsx).
 *
 * Lê: os cadastros. Grava: api.salvarRepresentante, api.salvarImagem, api.removerImagem, api.excluirRepresentante.
 * Usado por: telas/Cadastros.tsx.
 */
import { useEffect, useRef, useState } from 'react';
import { Avatar } from '../../componentes/Avatar.tsx';
import { EscolherImagem } from '../../componentes/EscolherImagem.tsx';
import { api } from '../../dados/api.ts';
import { linhaDoTempoDosCodigos } from '../../dominio/identificacao.ts';
import type { Cadastros as TipoCadastros, Fonte, Mes, Representante } from '../../dominio/tipos.ts';
import { descreverPeriodo, porNome, type Executar } from './comum.ts';
import { TrocaDeRepresentante } from './TrocaDeRepresentante.tsx';

interface Props {
  mes: Mes;
  cadastros: TipoCadastros;
  executar: Executar;
}

/** O que a tela está fazendo agora (um painel aberto por vez). */
type Aberto =
  | { tipo: 'novo'; nome: string; codigoWinthor: string; nomeSap: string; entraNoRanking: boolean }
  | { tipo: 'editar'; id: number; nome: string; ativo: boolean; entraNoRanking: boolean; trocandoFoto: boolean }
  | { tipo: 'saiu'; id: number }
  | null;

export function ListaDeRepresentantes({ mes, cadastros, executar }: Props) {
  const [aberto, setAberto] = useState<Aberto>(null);
  // O painel aberto (novo, editar, saiu) fica em cima da tabela: rola a tela até ele.
  const painel = useRef<HTMLDivElement>(null);
  const qualAberto = aberto ? `${aberto.tipo}-${'id' in aberto ? aberto.id : ''}` : '';
  useEffect(() => {
    if (qualAberto) painel.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [qualAberto]);
  const linhasDoTempo = { winthor: linhaDoTempoDosCodigos(cadastros.codigos, 'winthor'), 'sap-bauny': linhaDoTempoDosCodigos(cadastros.codigos, 'sap-bauny') };
  const ligacoesDe = (r: Representante, fonte: Fonte) => linhasDoTempo[fonte].flatMap((linha) => linha.ligacoes.filter((ligacao) => ligacao.representanteId === r.id));
  const descreverLigacoes = (r: Representante, fonte: Fonte) =>
    ligacoesDe(r, fonte)
      .map((ligacao) => (ligacao.desde === null && ligacao.ate === null ? ligacao.codigo : `${ligacao.codigo} (${descreverPeriodo(ligacao)})`))
      .join(' · ');
  const lista = [...cadastros.representantes].sort((a, b) => Number(b.ativo) - Number(a.ativo) || Number(b.entraNoRanking) - Number(a.entraNoRanking) || porNome(a, b));
  const representante = (id: number) => cadastros.representantes.find((r) => r.id === id) ?? null;

  async function criar() {
    if (aberto?.tipo !== 'novo') return;
    const codigos = [
      ...(aberto.codigoWinthor.trim() ? [{ fonte: 'winthor' as const, codigo: aberto.codigoWinthor.trim(), desde: null }] : []),
      ...(aberto.nomeSap.trim() ? [{ fonte: 'sap-bauny' as const, codigo: aberto.nomeSap.trim(), desde: null }] : []),
    ];
    const nome = aberto.nome.trim();
    let novo: Representante | undefined;
    const certo = await executar(async () => {
      const cadastrosNovos = await api.salvarRepresentante({ nome, ativo: true, entraNoRanking: aberto.entraNoRanking, codigos });
      novo = cadastrosNovos.representantes.find((r) => r.nome.toLowerCase() === nome.toLowerCase());
      return cadastrosNovos;
    }, `Cadastro de ${nome} criado${codigos.length > 0 ? ' com os códigos' : ''}. Se quiser, ponha a foto abaixo.`);
    // Já abre a foto do cadastro novo (opcional).
    if (certo) setAberto(novo ? { tipo: 'editar', id: novo.id, nome: novo.nome, ativo: novo.ativo, entraNoRanking: novo.entraNoRanking, trocandoFoto: true } : null);
  }

  async function salvarEdicao() {
    if (aberto?.tipo !== 'editar') return;
    const { id, nome, ativo, entraNoRanking } = aberto;
    if (await executar(() => api.salvarRepresentante({ id, nome: nome.trim(), ativo, entraNoRanking }), `Cadastro de ${nome.trim()} salvo.`)) setAberto(null);
  }

  async function excluir(r: Representante) {
    if (!window.confirm(`Excluir o cadastro de ${r.nome}? Use só para cadastro feito por engano. Se a pessoa saiu da equipe, use "Saiu da equipe".`)) return;
    if (await executar(() => api.excluirRepresentante(r.id), `Cadastro de ${r.nome} excluído.`)) setAberto(null);
  }

  function removerFoto(r: Representante) {
    if (!window.confirm(`Remover a foto de ${r.nome}? Voltam a aparecer as iniciais.`)) return;
    void executar(() => api.removerImagem(`representante-${r.id}`), `Foto de ${r.nome} removida.`);
  }

  /** Fecha só a troca de foto deste painel (o painel pode ter mudado enquanto a foto subia). */
  const fecharFoto = (id: number) => setAberto((atual) => (atual?.tipo === 'editar' && atual.id === id ? { ...atual, trocandoFoto: false } : atual));

  const editando = aberto?.tipo === 'editar' ? representante(aberto.id) : null;
  const saindo = aberto?.tipo === 'saiu' ? representante(aberto.id) : null;

  return (
    <section className="cartao pilha">
      <div className="pagina-titulo">
        <div>
          <h2>Representantes</h2>
          <p className="subtitulo">
            A equipe. Quem fica fora do ranking (ex.: o sócio) não entra nas metas nem na participação. Quando alguém sai e outra pessoa assume os clientes,
            use <strong>Saiu da equipe</strong>: os códigos e as metas passam para quem entra, a partir do mês da troca.
          </p>
        </div>
        <button type="button" className="botao" onClick={() => setAberto({ tipo: 'novo', nome: '', codigoWinthor: '', nomeSap: '', entraNoRanking: true })}>
          Novo representante
        </button>
      </div>

      <div ref={painel} className="ancora-do-painel" />
      {aberto?.tipo === 'novo' ? (
        <div className="pilha antes-depois">
          <h3>Novo representante</h3>
          <div className="linha-campos">
            <label>
              Nome
              <input value={aberto.nome} onChange={(e) => setAberto({ ...aberto, nome: e.target.value })} autoFocus />
            </label>
            <label>
              Código no Winthor (se já tiver)
              <input value={aberto.codigoWinthor} onChange={(e) => setAberto({ ...aberto, codigoWinthor: e.target.value })} placeholder="ex.: 57" style={{ width: 120 }} />
            </label>
            <label>
              Nome no SAP (se vender Bauny)
              <input value={aberto.nomeSap} onChange={(e) => setAberto({ ...aberto, nomeSap: e.target.value })} placeholder="ex.: GO - MEIRELES - Nome Sobrenome" style={{ width: 300, maxWidth: '100%' }} />
            </label>
            <label className="campo-checagem">
              <input type="checkbox" checked={aberto.entraNoRanking} onChange={(e) => setAberto({ ...aberto, entraNoRanking: e.target.checked })} />
              Entra no ranking e nas metas
            </label>
          </div>
          <p className="texto-apagado texto-pequeno">Está entrando no lugar de alguém? Cancele e use "Saiu da equipe" na pessoa que saiu: assim os clientes e as metas passam juntos.</p>
          <div className="linha-campos">
            <button type="button" className="botao" onClick={() => void criar()} disabled={aberto.nome.trim() === ''}>
              Cadastrar
            </button>
            <button type="button" className="botao-secundario" onClick={() => setAberto(null)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      {aberto?.tipo === 'editar' && editando ? (
        <div className="pilha antes-depois">
          <h3>Editar {editando.nome}</h3>
          <div className="linha-foto">
            <Avatar nome={editando.nome} foto={editando.foto} tamanho={72} />
            {aberto.trocandoFoto ? (
              <EscolherImagem
                forma="foto"
                aoSalvar={async (imagem) => {
                  const certo = await executar(() => api.salvarImagem(`representante-${editando.id}`, imagem), `Foto de ${editando.nome} salva.`);
                  if (certo) fecharFoto(editando.id);
                  return certo;
                }}
                aoCancelar={() => fecharFoto(editando.id)}
              />
            ) : (
              <div className="linha-campos">
                <button type="button" className="botao-secundario" onClick={() => setAberto({ ...aberto, trocandoFoto: true })}>
                  {editando.foto ? 'Trocar foto' : 'Pôr foto'}
                </button>
                {editando.foto ? (
                  <button type="button" className="botao-link" onClick={() => removerFoto(editando)}>
                    remover foto
                  </button>
                ) : null}
              </div>
            )}
          </div>
          <div className="linha-campos">
            <label>
              Nome
              <input value={aberto.nome} onChange={(e) => setAberto({ ...aberto, nome: e.target.value })} />
            </label>
            <label className="campo-checagem">
              <input type="checkbox" checked={aberto.ativo} onChange={(e) => setAberto({ ...aberto, ativo: e.target.checked })} />
              Ativo
            </label>
            <label className="campo-checagem">
              <input type="checkbox" checked={aberto.entraNoRanking} onChange={(e) => setAberto({ ...aberto, entraNoRanking: e.target.checked })} />
              Entra no ranking e nas metas
            </label>
          </div>
          <div className="linha-campos">
            <button type="button" className="botao" onClick={() => void salvarEdicao()} disabled={aberto.nome.trim() === ''}>
              Salvar
            </button>
            <button type="button" className="botao-secundario" onClick={() => setAberto(null)}>
              Fechar
            </button>
            <button type="button" className="botao-perigo" onClick={() => void excluir(editando)}>
              Excluir (cadastro errado)
            </button>
          </div>
        </div>
      ) : null}

      {saindo ? <TrocaDeRepresentante key={`${saindo.id}-${mes}`} sai={saindo} mes={mes} cadastros={cadastros} executar={executar} aoFechar={() => setAberto(null)} /> : null}

      <div className="tabela-rolavel">
        <table className="tabela">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Código no Winthor</th>
              <th className="so-tela-grande-celula">Nome no SAP</th>
              <th>Situação</th>
              <th className="so-tela-grande-celula"></th>
            </tr>
          </thead>
          <tbody>
            {lista.map((r) => {
              const acoes = (
                <div className="linha-campos acoes-da-linha">
                  <button
                    type="button"
                    className="botao-secundario"
                    onClick={() => setAberto({ tipo: 'editar', id: r.id, nome: r.nome, ativo: r.ativo, entraNoRanking: r.entraNoRanking, trocandoFoto: false })}
                  >
                    Editar
                  </button>
                  {r.ativo ? (
                    <button type="button" className="botao-secundario" onClick={() => setAberto({ tipo: 'saiu', id: r.id })}>
                      Saiu da equipe
                    </button>
                  ) : null}
                </div>
              );
              const codigosWinthor = descreverLigacoes(r, 'winthor');
              const nomesSap = descreverLigacoes(r, 'sap-bauny');
              return (
                <tr key={r.id}>
                  <td>
                    <span className="nome-com-foto">
                      <Avatar nome={r.nome} foto={r.foto} tamanho={32} />
                      {r.nome}
                    </span>
                    {/* No celular os botões ficam aqui, embaixo do nome (a última coluna sai da tela). */}
                    <div className="so-celular-bloco">{acoes}</div>
                  </td>
                  <td>{codigosWinthor !== '' ? codigosWinthor : r.ativo ? <span className="etiqueta etiqueta-alerta">! falta o código</span> : <span className="texto-apagado">—</span>}</td>
                  <td className="texto-pequeno so-tela-grande-celula">{nomesSap !== '' ? nomesSap : <span className="texto-apagado">—</span>}</td>
                  <td>
                    <span className={r.ativo ? 'etiqueta etiqueta-bom' : 'etiqueta etiqueta-apagada'}>{r.ativo ? 'Ativo' : 'Inativo'}</span>{' '}
                    {!r.entraNoRanking ? <span className="etiqueta">fora do ranking</span> : null}
                  </td>
                  <td className="so-tela-grande-celula">{acoes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
